import React, { useEffect, useRef, useState } from 'react';
import { Dialog, IconButton } from '@mui/material';
import styled from '@emotion/styled';
import XIcon from '@/public/icons/close_black.svg';
import Spinner from '@/components/@IntranetLibrary/Spinner';

type Props = {
  open: boolean;
  fileName: string;
  checking: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (password: string) => void;
};

const PasswordPromptDialog = ({
  open,
  fileName,
  checking,
  error,
  onClose,
  onSubmit,
}: Props) => {
  const [password, setPassword] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const submit = () => {
    if (checking) return;
    if (!password) return;
    onSubmit(password);
  };

  return (
    <StyledDialog
      open={open}
      onClose={(_, reason) => {
        if (checking && (reason === 'escapeKeyDown' || reason === 'backdropClick')) return;
        onClose();
      }}
      maxWidth={'xs'}
      fullWidth
    >
      <Header>
        <Title>PDF 암호 입력</Title>
        <IconButton onClick={onClose} disabled={checking} size={'small'}>
          <XIcon width={20} height={20} />
        </IconButton>
      </Header>
      <Body>
        <Description>
          {fileName}
          <br />
          이 PDF 는 암호로 보호되어 있습니다. 첨부하려면 암호를 입력해 주세요.
        </Description>
        <PasswordInput
          ref={inputRef}
          type="password"
          value={password}
          placeholder="암호 입력"
          disabled={checking}
          $invalid={!!error}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
        {error && <ErrorText>{error}</ErrorText>}
      </Body>
      <Actions>
        <CancelBtn onClick={onClose} disabled={checking}>
          취소
        </CancelBtn>
        <ConfirmBtn onClick={submit} disabled={checking || !password}>
          {checking ? <Spinner theme={'white'} /> : '확인'}
        </ConfirmBtn>
      </Actions>
    </StyledDialog>
  );
};

export default PasswordPromptDialog;

const StyledDialog = styled(Dialog)`
  font-family: var(--font-Pretendard);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #e7e9ec;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 8px;
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: #46505f;
  word-break: break-all;
`;

const PasswordInput = styled.input<{ $invalid: boolean }>`
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#F55060' : '#e7e9ec')};
  font-size: 14px;
  outline: none;
  :focus {
    border-color: ${({ $invalid }) => ($invalid ? '#F55060' : '#000')};
  }
  :disabled {
    background: #f9fafb;
  }
`;

const ErrorText = styled.div`
  color: #f55060;
  font-size: 12px;
  line-height: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 24px 24px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e7e9ec;
  :disabled {
    opacity: 0.5;
  }
`;

const ConfirmBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  background-color: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled {
    background-color: #d1d8e2;
  }
`;
